/**
 * USVI Pirate's Guild — Centralized marketing copy.
 *
 * Storing high-visibility strings here prevents the V1 token-economy language
 * from being re-introduced via duplicated copy in multiple components. New
 * pages should import from this file rather than hardcoding hero/CTA strings.
 *
 * Internal note for contributors: see `BANNED_PHRASES` at the bottom of this
 * file for language to avoid. CI / lint enforcement is planned in Phase 8.
 */

export const BRAND = Object.freeze({
  name: "USVI Pirate's Guild",
  shortName: "Pirate's Guild",
  positioning:
    "The USVI Pirate's Guild is a travel perks, local discovery, and business promotion network for the U.S. Virgin Islands. Travelers can join free to discover local deals, maps, events, and experiences. Businesses can join free to be discovered by travelers. Paid upgrades give travelers better perks and give businesses more visibility, promotion, analytics, and booking opportunities.",
});

export const HOME_HERO = Object.freeze({
  headline: "USVI Pirate's Guild",
  subheadline:
    "Join the Pirate's Guild free to find local restaurants, shops, tours, events, maps, and member perks across the U.S. Virgin Islands. Upgrade only when you want premium savings, VIP access, and insider travel help.",
  subheadline2: "Discover USVI Deals, Local Experiences, and Hidden Island Treasures!",
  primaryCta: { label: "Join Free as a Traveler", href: "/signup?audience=traveler" },
  secondaryCta: {
    label: "List Your Business Free",
    href: "/signup?audience=business",
  },
  tertiaryCta: { label: "View Premium Passes", href: "/traveler-passes" },
  quaternaryCta: {
    label: "Promote Your Business",
    href: "/for-businesses",
  },
  subnote:
    "Standard Guild deals are free to use — no Doubloons needed. Doubloons are bonus loyalty rewards you earn as you explore.",
});

export const HOW_IT_WORKS = Object.freeze({
  title: "How The Guild Works",
  travelers: {
    heading: "For Travelers",
    steps: [
      "Join free.",
      "Discover local deals, maps, events, and experiences.",
      "Upgrade only if you want premium perks, VIP access, concierge help, and extra savings.",
    ],
  },
  businesses: {
    heading: "For Businesses",
    steps: [
      "List your business free.",
      "Offer a Guild deal or perk to attract travelers.",
      "Upgrade only if you want featured placement, promotion, analytics, sponsored campaigns, or booking support.",
    ],
  },
});

export const DOUBLOONS = Object.freeze({
  title: "Doubloons: Rewards for Exploring",
  intro:
    "Doubloons are the Pirate's Guild reward points. You earn them as you explore the islands, support local businesses, attend events, complete treasure hunts, and refer friends. They are not required to access standard Guild deals.",
  earnHeading: "How to Earn Doubloons",
  earnList: [
    "Join the Guild",
    "Visit participating businesses",
    "Scan QR codes at partner locations",
    "Refer friends",
    "Attend Guild events",
    "Complete treasure hunts",
    "Book experiences through the Guild",
    "Participate in sponsored promotions",
  ],
  useHeading: "How to Use Doubloons",
  useList: [
    "Redeem for bonus perks",
    "Claim member-only rewards",
    "Unlock treasure hunt rewards",
    "Get event upgrades",
    "Claim sponsored partner rewards",
    "Access limited-time fun extras",
    "Redeem for select merch or digital badges",
  ],
  disclaimer:
    "Doubloons are a bonus reward system. Standard Guild deals do not require Doubloons.",
});

export const FOR_BUSINESSES = Object.freeze({
  title: "Grow Your Business With The Guild",
  positioning:
    "Join free to be discovered by travelers. Upgrade when you want more visibility, better promotion, campaign tracking, and measurable customer traffic.",
  bookingsCommissionNote:
    "Only pay booking commissions when the Guild helps generate a confirmed booking or paid event registration.",
});

export const SAVINGS_CALCULATOR_DEFAULTS = Object.freeze({
  title: "Will the pass pay for itself?",
  exampleSavings: [
    { label: "Lunch", amountUsd: 8 },
    { label: "Tour", amountUsd: 20 },
    { label: "Souvenirs", amountUsd: 12 },
    { label: "Dinner", amountUsd: 15 },
  ],
});

export const EVENTS_COPY = Object.freeze({
  travelerBlurb:
    "Find local happenings, pirate-themed meetups, treasure hunts, food specials, tours, and VIP island experiences.",
  businessBlurb:
    "Promote your event to travelers, locals, and Guild members through featured listings, sponsored campaigns, and ticketing support.",
});

/**
 * Phrases that must NOT appear in user-facing copy. CI lint planned for Phase 8.
 * Match is case-insensitive; the regex form is used by `scripts/audit-copy.js`.
 */
export const BANNED_PHRASES = Object.freeze([
  /buy\s+tokens/i,
  /purchase\s+tokens/i,
  /tokens?\s+to\s+(use|access|unlock)/i,
  /free\s+advertising/i,
  /only\s+\$\d+.*\bfree\b/i,
  /200\+?\s+businesses/i,
  /tokens?\s+(are\s+)?required/i,
]);
